import { yupResolver } from "@hookform/resolvers/yup";
import { Close, DeleteOutline as DeleteIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import { practiceProfileApi } from "apis";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import { useToast } from "contexts/ToastContext";
import { AppointmentData, useAppointments } from "hooks/useAppointments";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { themeColors } from "theme/colorTokens";
import {
  AppointmentFormData,
  appointmentSchema,
} from "validations/appointmentSchemas";

interface EditAppointmentModalProps {
  open: boolean;
  onClose: (isRefresh: boolean) => void;
  appointment?: AppointmentData | null;
}

const EditAppointmentModal: React.FC<EditAppointmentModalProps> = ({
  open,
  onClose,
  appointment,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const { deleteAppointment, updateAppointment, error, loading } =
    useAppointments();
  const [appointmentId, setAppointmentId] = useState<string | undefined>();
  const [patientName, setPatientName] = useState<string>("");
  const [patientEmail, setPatientEmail] = useState<string>("");
  const [colorCode, setColorCode] = useState<number | undefined>();

  const { data: provider = null, isLoading: providerLoading } = useQuery({
    queryKey: ["provider"],
    queryFn: async () => {
      const result = await practiceProfileApi.getProviderProfile();
      return result || null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const formData = useForm({
    resolver: yupResolver(appointmentSchema),
    defaultValues: {
      patientId: "",
      providerId: "",
      startDate: new Date(),
      startTime: new Date(),
      endTime: new Date(),
      colorCode: undefined,
      notes: "",
    },
  });

  useEffect(() => {
    if (error) {
      showError("Error updating appointment:", error);
    }
  }, [error, showError]);

  // Populate form when appointment changes
  useEffect(() => {
    if (appointment) {
      setAppointmentId(appointment.id);
      setPatientName(appointment.patientName || "");
      setPatientEmail(appointment.patientEmail || "");
      setColorCode(appointment.colorCode);

      formData.reset({
        patientId: appointment.patientId || "",
        providerId: appointment.providerId || "",
        startDate: appointment.startDate
          ? new Date(appointment.startDate)
          : new Date(),
        startTime: appointment.startTime
          ? new Date(appointment.startTime)
          : new Date(),
        endTime: appointment.endTime
          ? new Date(appointment.endTime)
          : new Date(),
        colorCode: appointment.colorCode,
        notes: appointment.notes || "",
      });
      formData.clearErrors();
    } else {
      formData.reset({
        patientId: "",
        providerId: "",
        startDate: new Date(),
        startTime: new Date(),
        endTime: new Date(),
        colorCode: undefined,
        notes: "",
      });
      setAppointmentId(undefined);
      setPatientName("");
      setPatientEmail("");
      setColorCode(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  const providerName = useMemo(() => {
    return provider ? `${provider.firstName} ${provider.lastName}` : "";
  }, [provider]);

  const handleInputChange = useCallback(
    <K extends keyof AppointmentFormData>(
      field: K,
      value: AppointmentFormData[K]
    ) => {
      formData.setValue(field as keyof AppointmentFormData, value);

      // Clear error when user starts typing
      formData.clearErrors(field);

      // Validate that end time is still after start time
      if (
        field === "startTime" &&
        value &&
        formData.getValues("endTime") &&
        formData.getValues("startDate")
      ) {
        const startDateTime = new Date(formData.getValues("startDate"));
        startDateTime.setHours((value as Date).getHours());
        startDateTime.setMinutes((value as Date).getMinutes());

        const endDateTime = new Date(formData.getValues("startDate"));
        endDateTime.setHours(formData.getValues("endTime").getHours());
        endDateTime.setMinutes(formData.getValues("endTime").getMinutes());

        if (endDateTime <= startDateTime) {
          formData.setError("endTime", {
            message: "End time must be after start time",
          });
        }
      }
    },
    [formData]
  );

  const handleStartTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        handleInputChange("startTime", time);
      }
    },
    [handleInputChange]
  );

  const handleEndTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        handleInputChange("endTime", time);
      }
    },
    [handleInputChange]
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        handleInputChange("startDate", date);
      }
    },
    [handleInputChange]
  );

  const handleClose = useCallback(
    (isRefresh: boolean = false) => {
      formData.clearErrors();
      onClose(isRefresh);
    },
    [formData, onClose]
  );

  const onSubmit = useCallback(
    async (data: AppointmentFormData) => {
      if (!appointmentId) return;

      const appointmentData: AppointmentData = {
        id: appointmentId,
        patientId: data.patientId,
        providerId: data.providerId,
        startDate: data.startDate,
        startTime: data.startTime,
        endTime: data.endTime,
        colorCode: colorCode || data.colorCode,
        notes: data.notes,
        patientName,
        patientEmail,
      };
      await updateAppointment(appointmentId, appointmentData);
      showSuccess(
        "Appointment Updated",
        "Appointment has been successfully updated."
      );
      handleClose(true);
    },
    [
      appointmentId,
      colorCode,
      patientName,
      patientEmail,
      updateAppointment,
      showSuccess,
      handleClose,
    ]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!appointmentId) return;
    await deleteAppointment(appointmentId);
    showSuccess(
      "Appointment Deleted",
      "Appointment has been successfully deleted."
    );
    handleClose(true);
    setConfirmOpen(false);
  }, [appointmentId, deleteAppointment, showSuccess, handleClose]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
            },
          },
        }}
      >
        <FormProvider {...formData}>
          <form onSubmit={formData.handleSubmit(onSubmit)}>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "18px 24px",
                borderBottom: "none",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: themeColors.text.primary,
                  fontSize: "24px",
                }}
              >
                Edit Appointment
              </Typography>
              <IconButton
                onClick={() => handleClose(false)}
                size="small"
                sx={{
                  color: themeColors.text.primary,
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent
              sx={{
                padding: "24px",
                paddingTop: "16px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <TextField
                  label="Patient"
                  fullWidth
                  value={patientName}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
                {providerLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <TextField
                    label="Staff"
                    fullWidth
                    value={providerName}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />
                )}

                <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
                  <DatePicker
                    label="Date"
                    value={formData.getValues("startDate")}
                    onChange={handleDateChange}
                    format="EEEE, MMMM d"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(formData.formState.errors.startDate),
                        helperText:
                          formData.formState.errors.startDate?.message,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                          },
                        },
                      },
                    }}
                  />

                  <Box sx={{ flex: 1 }}>
                    <TimePicker
                      label="Start Time"
                      value={formData.getValues("startTime")}
                      onChange={handleStartTimeChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(formData.formState.errors.startTime),
                          helperText:
                            formData.formState.errors.startTime?.message,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TimePicker
                      label="End Time"
                      value={formData.getValues("endTime")}
                      onChange={handleEndTimeChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(formData.formState.errors.endTime),
                          helperText:
                            formData.formState.errors.endTime?.message,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <FormControl fullWidth>
                    <TextField
                      label="Note"
                      name="notes"
                      multiline
                      rows={4}
                      value={formData.watch("notes")}
                      onChange={(e) =>
                        formData.setValue("notes", e.target.value)
                      }
                      fullWidth
                      slotProps={{
                        input: {
                          name: "notes",
                          inputProps: {
                            maxLength: 2000,
                          },
                        },
                        formHelperText: {
                          sx: {
                            marginLeft: 0,
                            marginRight: 0,
                            display: "flex",
                            justifyContent: "space-between",
                          },
                        },
                      }}
                      error={Boolean(formData.formState.errors.notes)}
                      helperText={
                        formData.formState.errors.notes?.message ||
                        `${formData.getValues("notes")?.length || 0}/2000`
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </FormControl>
                </Box>
              </Box>
            </DialogContent>
            <Divider />

            <DialogActions
              sx={{
                padding: "16px 24px 24px 24px",
                justifyContent: "space-between",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <IconButton
                onClick={() => setConfirmOpen(true)}
                disabled={loading}
                sx={{
                  color: themeColors.text.error,
                  border: `1px solid ${themeColors.border.dark}`,
                  "&:hover": {
                    color: themeColors.text.errorDark,
                    border: `1px solid ${themeColors.border.darker}`,
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
              <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
                <Button
                  onClick={() => handleClose(false)}
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    borderColor: themeColors.border.main,
                    color: themeColors.text.primary,
                    padding: "12px 32px",
                    borderRadius: "24px",
                    fontSize: "16px",
                    fontWeight: 600,
                    textTransform: "none",
                    minWidth: "140px",
                    "&:hover": {
                      borderColor: themeColors.primary.main,
                      backgroundColor: themeColors.primary.light + "10",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !appointmentId}
                  sx={{
                    backgroundColor: themeColors.primary.main,
                    color: themeColors.text.inverse,
                    padding: "12px 32px",
                    borderRadius: "24px",
                    fontSize: "16px",
                    fontWeight: 600,
                    textTransform: "none",
                    minWidth: "140px",
                    "&:hover": {
                      backgroundColor: themeColors.primary.dark,
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : "Save"}
                </Button>
              </Box>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      <ConfirmationPopup
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Appointment"
        message="Are you sure you want to delete this appointment? This action cannot be undone."
        confirmText="Delete"
        confirmColor={themeColors.error[500]}
        confirmHoverColor={themeColors.error[600]}
        confirmTextHoverColor={themeColors.text.inverse}
        maxWidth="sm"
        fullWidth
        paperSx={{
          borderRadius: "8px",
        }}
        loading={loading}
        disableBackdropClick
        disableEscapeKeyDown
        showCloseButton={!loading}
      />
    </LocalizationProvider>
  );
};

export default EditAppointmentModal;
